<template>
  <div>
    <div class="text-right mt-3">
      <action-button
        v-if="showRestartStatus"
        class="btn-block-xs-only mr-3"
        i="fas fa-undo"
        text="Regresar Estatus"
        variant="outline-success"
        @click="restartEstatusHandler"
      />

      <action-button
        v-if="showRemoveFiles"
        class="outline-danger btn-block-xs-only mr-3"
        i="fas fa-times-circle"
        text="Eliminar Archivos de Pagos"
        @click="removeFilesHandler"
      />
    </div>
    <div class="container-fluid mx-0 px-0 w-100">
      <div class="row no-gutters">
        <div :class="isThisJornalPaid ? 'col-md-8' : 'col-md-12'">
          <section-title has-line title="Información de Jornal" />
        </div>
        <div class="col-md-4 d-flex justify-content-md-end mb-4 mb-md-0">
          <template v-if="isThisJornalPaid">
            <span class="d-flex align-items-center">
              <label
                class="color-secondary-6 font-14 font-weight-normal mb-0 mr-1"
              >
                Fecha Pagado:
              </label>
              <span>{{ value.paymentDate | date }}</span>
            </span>
          </template>
        </div>
      </div>
    </div>
    <div class="bg-secondary-3 rounded p-2">
      <div class="row mt-2 mb-1 mx-1">
        <div class="col-6 col-md-3">
          <div class="d-flex align-items-center">
            <div class="journal-icon-dot">
              <img
                alt=""
                class="journal-icon-img"
                src="@/assets/img/invoice-file.svg"
              />
            </div>
            <div>
              <div class="color-gray-6 font-14 font-weight-normal">
                Número de Jornal:
              </div>
              <div class="color-black font-weight-bold font-16 mt-1">
                {{ value.paymentJournalNumber }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3 mt-3 mt-md-0">
          <div class="color-gray-6 font-14 font-weight-normal">
            Estatus de Jornal:
          </div>
          <div class="color-black font-weight-bold font-16 mt-1">
            <i
              class="fas fa-circle mr-1"
              :class="`color-${getStatusColor(value.paymentJournalStatusId)}`"
            />
            {{ value.paymentJournalStatusName }}
          </div>
        </div>
        <div class="col-6 col-md-3 mt-3 mt-md-0">
          <div class="color-secondary-6 font-14 font-weight-normal">
            Fecha Creado:
          </div>
          <div class="color-black font-weight-bold font-16 mt-1">
            {{ value.createDate | date }}
          </div>
        </div>
        <div
          v-if="showJournalType"
          class="col-6 col-md-3 d-flex align-items-center justify-content-end mt-3 mt-md-0"
        >
          <span
            class="journal-type-badge"
            :class="getJournalTypeClass(value.paymentJournalTypeId)"
          >
            {{ getJournalTypeLabel(value.paymentJournalTypeId) }}
          </span>
        </div>
      </div>
      <div class="row my-2 py-3 rounded bg-white mx-1">
        <div class="col-6 col-md-3">
          <div class="journal-icon-offset">
            <div class="color-secondary-6 font-14 font-weight-normal">
              Pagos:
            </div>
            <div class="color-black font-weight-bold font-16 mt-1">
              {{ value.paymentCount }}
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="color-secondary-6 font-14 font-weight-normal">
            Procesado:
          </div>
          <div class="color-black font-weight-bold font-16 mt-1">
            {{ value.processedAmount | price }}
          </div>
        </div>
        <div class="col-6 col-md-3 mt-3 mt-md-0">
          <div class="color-secondary-6 font-14 font-weight-normal">
            Pendiente:
          </div>
          <div class="color-black font-weight-bold font-16 mt-1">
            {{ value.pendingAmount | price }}
          </div>
        </div>
        <div class="col-6 col-md-3 d-flex justify-content-end mt-3 mt-md-0">
          <div class="journal-total-content">
            <div class="color-secondary-6 font-14 font-weight-normal">
              Total:
            </div>
            <div class="color-black font-weight-bold font-16 mt-1">
              {{ value.paymentTotalAmount | price }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import paymentJournalStatus from '@/utils/constants/paymentJournalStatus.js';
import paymentJournalType from '@/utils/constants/paymentJournalType.js';

export default {
  name: 'JournalInfo',
  components: {
    SectionTitle,
    ActionButton,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    showJournalType: {
      type: Boolean,
      default: false,
    },
    hasElectronicTransferJornalPayment: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    getJournalTypeLabel() {
      return (typeId) =>
        typeId === paymentJournalType.ACH
          ? 'ACH - Sistema Financiero'
          : 'Banco';
    },
    getJournalTypeClass() {
      return (typeId) => ({
        'journal-type-badge--ach': typeId === paymentJournalType.ACH,
        'journal-type-badge--banco': typeId !== paymentJournalType.ACH,
      });
    },
    getStatusColor() {
      return (statusId) => {
        switch (statusId) {
          case paymentJournalStatus.Nuevo:
            return 'secondary-23';
          case paymentJournalStatus.Procesado:
          case paymentJournalStatus.ArchivoGenerado:
            return 'secondary-36';
          case paymentJournalStatus.Pagado:
            return 'green-light';
          default:
            return 'secondary-23';
        }
      };
    },
    isACHType() {
      return (
        this.hasElectronicTransferJornalPayment &&
        this.value.paymentJournalTypeId === paymentJournalType.ACH
      );
    },
    showRestartStatus() {
      return (
        this.value.paymentJournalStatusId == paymentJournalStatus.Procesado &&
        !this.isACHType
      );
    },
    showRemoveFiles() {
      return (
        this.value.paymentJournalStatusId ==
          paymentJournalStatus.ArchivoGenerado &&
        this.userHasPermissions(
          'menu.scholarship.jornalpayment.removefilespayments'
        )
      );
    },
    isThisJornalPaid() {
      return this.value.paymentJournalStatusId === paymentJournalStatus.Pagado;
    },
  },
  methods: {
    restartEstatusHandler() {
      this.$emit('restart-status');
    },
    updateJournalHandler() {
      this.$emit('update-journal');
    },
    removeFilesHandler() {
      this.$emit('remove-files');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';

.custom-icon-view-size {
  width: 100px !important;
  height: 100px !important;
}

.journal-icon-offset {
  padding-left: 32px;
}

.journal-total-content {
  width: 152px;
}

.journal-icon-dot {
  box-shadow: 0 3px 6px #00000029;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  flex-shrink: 0;
  margin-right: 10px;
  justify-content: center;
  align-items: center;
  background: #fff;
  border: 3px solid #fff;
}

.journal-icon-img {
  width: 13px;
  height: 13px;
}

.journal-type-badge {
  display: inline-block;
  padding: 6px 8px;
  border-radius: 58px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid;
  white-space: nowrap;
  width: 152px;
  text-align: center;

  &--ach {
    color: map-get($colors, 'primary-1');
    border-color: map-get($colors, 'primary-1');
    background-color: map-get($colors, 'green-light-201');
  }

  &--banco {
    color: map-get($colors, 'primary-2');
    border-color: map-get($colors, 'primary-2');
    background-color: map-get($colors, 'secondary-19');
  }
}

@media only screen and (max-width: 768px) {
  .btn-block-xs-only {
    display: block;
    width: 100%;
    margin-bottom: 20px;
  }
  .custom-status-extended {
    width: 100% !important;
  }
}
</style>
